import React from 'react';

import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import Button from '@atlaskit/button/standard-button';
import AddonIcon from '@atlaskit/icon/glyph/addon';
import Tooltip from '@atlaskit/tooltip';
import { WebItems } from '@atlassian/bitbucket-connect-react';

import {
  RunPipelineContext,
  WebItemsWithoutRunPipeline,
} from 'src/components/pipelines/pages/modals/run-pipeline-modal';
import { Expander } from 'src/components/sidebar';
import { Commit as CommitType } from 'src/components/types';
import { commitTarget } from 'src/connect/targets';
import { useIntl } from 'src/hooks/intl';
import {
  getCurrentRepositoryOwnerName,
  getCurrentRepositorySlug,
} from 'src/selectors/repository-selectors';
import { getTargetUserKey } from 'src/selectors/user-selectors';

import messages from './commit-page.i18n';
import {
  CommitCardLinkText,
  CommitCardWrapper,
  ConnectLinkWrapper,
} from './commit-page.styled';

export const ExpandCommitConnect = ({
  commit,
}: {
  commit: CommitType;
}): JSX.Element => {
  const intl = useIntl();
  const principalId = useSelector(getTargetUserKey) || '';
  const repositoryOwner = useSelector(getCurrentRepositoryOwnerName) || '';
  const repositorySlug = useSelector(getCurrentRepositorySlug) || '';

  return (
    <Expander
      icon={<AddonIcon label={intl.formatMessage(messages.apps)} />}
      label={intl.formatMessage(messages.apps)}
    >
      <CommitCardWrapper>
        <WebItems
          location="org.bitbucket.commit.summary.info"
          principalId={principalId}
          target={commitTarget({
            repositoryOwner,
            repositorySlug,
            hash: commit.hash,
          })}
          component={ConnectLinkWrapper}
          loadingComponent={() => null}
          loadingFailedComponent={() => null}
        />
      </CommitCardWrapper>

      <RunPipelineContext.Consumer>
        {({ open }) => (
          <CommitCardWrapper>
            <Button
              appearance="link"
              onClick={() => open({ revision: commit.hash })}
              spacing="none"
            >
              <CommitCardLinkText>
                <FormattedMessage {...messages.runPipeline} />
              </CommitCardLinkText>
            </Button>
          </CommitCardWrapper>
        )}
      </RunPipelineContext.Consumer>

      <CommitCardWrapper>
        <WebItemsWithoutRunPipeline
          location="org.bitbucket.commit.summary.actions"
          principalId={principalId}
          target={commitTarget({
            repositoryOwner,
            repositorySlug,
            hash: commit.hash,
          })}
          component={ConnectLinkWrapper}
          loadingComponent={() => null}
          loadingFailedComponent={() => null}
        />
      </CommitCardWrapper>
    </Expander>
  );
};

export const CollapseCommitConnect = (): JSX.Element => {
  const intl = useIntl();
  return (
    <Tooltip content={intl.formatMessage(messages.apps)} position="left">
      <Button
        appearance="subtle"
        iconBefore={<AddonIcon label={intl.formatMessage(messages.apps)} />}
      />
    </Tooltip>
  );
};
